import { ReactElement } from 'react'

import { Button } from 'antd'
import { Formik, FormikHelpers, FieldArray } from 'formik'
import { useTranslation } from 'react-i18next'
import { array, object, string } from 'yup'

import { Flex } from 'components/core'
import { SELLER_BRAND_TYPE } from 'const/brands'
import { PAUSED, ENABLED } from 'const/resourceStates'
import { useCerebroApiRequest } from 'hooks'
import { Campaign } from 'types'
import message from 'utilities/message'

import CreateResourceModal from '../CreateResourceModal/CreateResourceModal'
import { BulkAddTextArea, Item } from '../Shared'

import {
    formatProductAdSubmitSummary,
    productAdSubmitToastType,
    shouldCloseProductAdModal,
} from './classifyProductAds'
import CreateProductAdForm from './CreateProductAdForm'
import { useValidator } from './localHooks'
import { FormValues, ProductAdField } from './localTypes'
import { submitProductAds } from './submitProductAds'

interface Props {
    campaign: Campaign | null
    isModalVisible: boolean
    onModalCancel: () => void
    toggleModalVisible: () => void
    reloadData: () => void
}

function CreateProductAdModal({
    campaign,
    isModalVisible,
    onModalCancel,
    reloadData,
    toggleModalVisible,
}: Props): ReactElement {
    const { t } = useTranslation(['campaigns', 'common'])
    const makeCerebroApiRequest = useCerebroApiRequest()
    const [validator, errorMsg] = useValidator(campaign)
    const isSellerBrandType = campaign?.profile.type === SELLER_BRAND_TYPE
    const bulkAddLabel = isSellerBrandType
        ? t('campaigns:createProductAd.skuLabel', 'SKU')
        : t('campaigns:createProductAd.asinLabel', 'ASIN')

    const closeModal = (resetForm: () => void): void => {
        resetForm()
        toggleModalVisible()
        reloadData()
    }

    const submitForm = async (
        { products }: FormValues,
        { resetForm }: FormikHelpers<FormValues>
    ): Promise<void> => {
        const counts = await submitProductAds({
            products,
            campaignId: campaign?.id,
            isSeller: isSellerBrandType,
            makeCerebroApiRequest,
        })
        const summary = formatProductAdSubmitSummary(
            counts,
            t,
            isSellerBrandType
        )

        const toastType = productAdSubmitToastType(counts, summary)
        if (toastType === 'success') {
            message.success(summary)
        } else if (toastType === 'warning') {
            message.warning(summary)
        } else if (toastType === 'error') {
            message.error(summary)
        }

        if (shouldCloseProductAdModal(counts)) {
            closeModal(resetForm)
        }
    }

    const initialValues: FormValues = { products: [] }

    const validationSchema = object().shape({
        products: array().of(
            object().shape({
                asin: string().required(),
                adGroupId: string().label('Ad Group Target').required(),
                state: string()
                    .label('State')
                    .matches(new RegExp(`${PAUSED}|${ENABLED}`))
                    .required(),
            })
        ),
    })

    const deserializeFieldArrayValue = (value: Item): ProductAdField => {
        return {
            asin: (value.product as string).trim(),
            adGroupId: campaign?.ad_groups[0]?.id ?? '',
            state: ENABLED,
        }
    }

    const handleFieldArrayPush = (
        value: Item,
        push: (obj: ProductAdField) => void
    ): void => {
        const product = deserializeFieldArrayValue(value)
        push(product)
    }

    return (
        <Formik
            initialValues={initialValues}
            enableReinitialize
            onSubmit={submitForm}
            validationSchema={validationSchema}
        >
            {({
                isSubmitting,
                resetForm,
                handleSubmit,
                values,
                setFieldValue,
                dirty,
                isValid,
            }) => (
                <CreateResourceModal
                    open={isModalVisible}
                    title={
                        <div>
                            {t(
                                'campaigns:createProductAd.title',
                                'Add {{type}}s',
                                { type: bulkAddLabel }
                            )}
                        </div>
                    }
                    onCancel={onModalCancel}
                    width={900}
                    footer={[
                        <Button
                            key={1}
                            onClick={() => {
                                resetForm()
                                toggleModalVisible()
                            }}
                            disabled={isSubmitting}
                            style={{ marginRight: 8 }}
                        >
                            {t('common:cancel', 'Cancel')}
                        </Button>,
                        <Button
                            key={2}
                            type="primary"
                            onClick={() => handleSubmit()}
                            loading={isSubmitting}
                            disabled={!dirty || !isValid}
                        >
                            {t(
                                'campaigns:createProductAd.submit',
                                'Create {{type}}s',
                                { type: bulkAddLabel }
                            )}
                        </Button>,
                    ]}
                >
                    <FieldArray
                        name="products"
                        render={(arrayHelpers) => (
                            <Flex direction="column" alignItems="normal">
                                <BulkAddTextArea
                                    errorMsg={errorMsg}
                                    label={`Product Ad ${bulkAddLabel}s`}
                                    loading={false}
                                    push={(item) => {
                                        handleFieldArrayPush(
                                            item,
                                            arrayHelpers.push
                                        )
                                    }}
                                    type="product"
                                    validator={validator}
                                />
                                <CreateProductAdForm
                                    campaign={campaign as Campaign}
                                    products={values.products}
                                    remove={arrayHelpers.remove}
                                    setFieldValue={setFieldValue}
                                />
                            </Flex>
                        )}
                    />
                </CreateResourceModal>
            )}
        </Formik>
    )
}

export default CreateProductAdModal
